import { ChangeDetectorRef, Component } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SeConstraintNotifierService } from '@se/constraint';
import { CultureService } from '@se/platform';
import { Logger } from '@se/se.ui.html5';
import { takeUntil } from 'rxjs/operators';

import { CpNumericControl, PropertyType } from '../../cp-configuration/view-model';
import { CpModelService } from '../../cp-model.service';
import { AbstractControlComponent } from '../abstract-control';

type ModelType = string | number | null | undefined;

@Component({
    selector: 'cp-numeric-input',
    templateUrl: './numeric.component.html',
    styleUrls: ['./numeric.component.scss'],
})
export class CpNumericComponent extends AbstractControlComponent {
    control!: CpNumericControl;

    private separatorInUse = this.cultureService.getDecimalSeparator();

    private model: number | null = null;

    constructor(
        private cultureService: CultureService,
        private modelService: CpModelService,
        private translate: TranslateService,
        cdr: ChangeDetectorRef,
        constraintNotifierService: SeConstraintNotifierService,
        logger: Logger
    ) {
        super(cdr, constraintNotifierService, logger);
    }

    getErrorMessage() {
        if (this.formControl?.hasError('max')) {
            return this.translate.instant('ValidationErrorMax', { p0: this.control.max });
        }
        if (this.formControl?.hasError('min')) {
            return this.translate.instant('ValidationErrorMin', { p0: this.control.min });
        }
    }

    onFocus() {
        this.focusin.emit();
    }

    protected onModelChange(value: string | null) {
        const { numModel, strModel } = this.control.getValues(value);
        this.model = numModel;
        this.formControl?.setValue(strModel, { emitEvent: false });

        if (this.model !== null && isNaN(this.model)) {
            return false;
        }

        const changed = this.control.onModelChange(this.modelService, this.model, !this.isDisabled);
        this.cdr.detectChanges();

        return changed;
    }

    protected init() {
        this.control.getEntityObservable(this.modelService).subscribe(this._entitySubject);

        const validators = this.control
            .getValidators()
            .concat(
                this.makeValidator(this._entitySubject, this.control.dataBinding.propertyName, this.control.required)
            );

        this.formControl = new FormControl(this.control.getInitialValue(this.modelService), validators);

        this.control
            .getModelObservable(this.modelService)
            .pipe(takeUntil(this.destroyed$))
            .subscribe((model) => {
                // Ignora update
                if (!this.isAcceptable(model)) {
                    this.logger?.error(`Invalid model update for ${this.control.name}: ${model}`);
                    return;
                }
                this.logger?.trace(`Model update ${this.control.name} ${model}`);
                this.updateFormControlValue(model);
            });
    }

    private isAcceptable(model: PropertyType | undefined): model is ModelType {
        return model === null || model === undefined || typeof model === 'number' || typeof model === 'string';
    }

    private updateFormControlValue(model: ModelType) {
        if (!this.formControl) {
            return;
        }

        let strModel: string | null = null;
        let numModel: number | null = null;

        if (model === undefined || model === null) {
            // Per type-guard
            strModel = null;
            numModel = null;
        } else if (typeof model === 'string') {
            let parsed = this.control.getValues(model);
            strModel = parsed.strModel;
            numModel = parsed.numModel;
            this.separatorInUse = parsed.separator ?? this.separatorInUse;
        } else {
            numModel = this.control.isFloatingPoint() ? model : Math.floor(model);
            strModel = numModel.toString().replace('.', this.separatorInUse);
        }

        if (numModel !== this.model) {
            this.model = numModel;
            this.formControl.setValue(strModel, { emitViewToModelChange: false });
        }
    }
}
